<script lang="ts" setup>
// 导入图片
import case1Image from '@/assets/images/product1000bit_mobile_case1.png'
import case2Image from '@/assets/images/product1000bit_mobile_case2.png'
import case3Image from '@/assets/images/product1000bit_mobile_case3.png'
import bgStability from '@/assets/images/product1000bit_bg_stability.png'
import bgArithmetic from '@/assets/images/product1000bit_arithmetic_bg.png'
import bgClock from '@/assets/images/product1000bit_bg_clock.png'
import mobileBgStability from '@/assets/images/product1000bit_mobile_bg_stability.png'
import mobileBgClock from '@/assets/images/product1000bit_bg_mobile_clock.png'
import mobileBgArithmetic from '@/assets/images/product1000bit_arithmetic_mobile_bg.png'
import mobileCaseBg from '@/assets/images/1000bg.png'
// 导入视频
// import video1000bit from '@/assets/images/1000bit/1000bit.webm'
// import videoClock from '@/assets/images/product1000bit_clock.webm'
// import video1000 from '@/assets/images/1000bit/1000.webm'
import video1000Mobile from '@/assets/images/1000bit/1000_mobile.webm'
// import MachineModel from '@/components/MachineModel.vue'
import { vLazyVideo } from '@/composables/useLazyVideo'
const { trackEvent } = useCnzz()

const { t } = useI18n()

definePageMeta({ layout: 'page' })
useHead({
  title: t('seo.product1000bit.title'),
  meta: [
    {
      name: 'description',
      content: t('seo.product1000bit.description'),
    },
    {
      name: 'keywords',
      content: t('seo.product1000bit.keywords'),
    },
    {
      property: 'og:title',
      content: t('seo.product1000bit.title'),
    },
    {
      property: 'og:description',
      content: t('seo.product1000bit.description'),
    },
    {
      property: 'og:type',
      content: 'website',
    },
    {
      property: 'og:image',
      content: 'https://www.qboson.com/favicon.ico',
    },
    {
      property: 'og:locale',
      content: 'zh_CN',
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      name: 'twitter:title',
      content: t('seo.product1000bit.title'),
    },
    {
      name: 'twitter:description',
      content: t('seo.product1000bit.description'),
    },
    {
      name: 'twitter:image',
      content: 'https://www.qboson.com/favicon.ico',
    },
    {
      name: 'author',
      content: '玻色量子',
    },
  ],
})

const videoStability = ref(
  'https://images-cloud.qboson.com/website/1000bit/product_mp4.mp4',
)

// 在线咨询弹窗逻辑
const showConsultDialog = ref(false)

// 打开弹窗
const openDialog = () => {
  showConsultDialog.value = true
  trackEvent('1000bit专用量子计算机', '点击', '联系我们按钮')
}

// 滚动淡入动画
const observerOptions = {
  threshold: 0.1,
  rootMargin: '0px 0px -50px 0px',
}

onMounted(() => {
  // 滚动淡入动画
  const sections = document.querySelectorAll('.fade-in-section')

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('fade-in-visible')
        // 触发子元素的顺序动画
        const items = entry.target.querySelectorAll('.fade-in-item')
        items.forEach((item) => {
          item.classList.add('fade-in-item-visible')
        })
        observer.unobserve(entry.target) // 只触发一次
      }
    })
  }, observerOptions)

  sections.forEach((section) => {
    observer.observe(section)
  })

  // 启动轮播图自动播放
  startAutoPlay()

  // 处理轮播图的初始状态
  nextTick(() => {
    const carousel = document.querySelector('.carousel-track') as HTMLElement
    if (carousel) {
      // 确保初始位置正确
      carousel.style.transform = `translateX(-${currentSlide.value * 320}px)`
    }
  })
})

onUnmounted(() => {
  if (autoPlayTimer.value) {
    clearInterval(autoPlayTimer.value)
  }
})

// Hero 按钮数据
const heroButtons = computed(() => [
  {
    key: 'contact',
    label: t('product1000bit.hero.btnContact'),
    to: '/product/productConsultation',
  },
  {
    key: 'compare',
    label: t('product1000bit.hero.btnCompare'),
    to: '/product/productSpecs',
  },
])

// 特性数据
const features = computed(() => [
  {
    key: 'computing',
    title: t('product1000bit.features.computing.title'),
    description: t('product1000bit.features.computing.description'),
  },
  {
    key: 'performance',
    title: t('product1000bit.features.performance.title'),
    description: t('product1000bit.features.performance.description'),
  },
  {
    key: 'measurement',
    title: t('product1000bit.features.measurement.title'),
    description: t('product1000bit.features.measurement.description'),
  },
  {
    key: 'algorithm',
    title: t('product1000bit.features.algorithm.title'),
    description: t('product1000bit.features.algorithm.description'),
  },
])

// 部署特性数据
const deploymentFeatures = computed(() => [
  {
    key: 'efficient',
    title: t('product1000bit.deployment.efficient.title'),
    description: t('product1000bit.deployment.efficient.description'),
  },
  {
    key: 'universal',
    title: t('product1000bit.deployment.universal.title'),
    description: t('product1000bit.deployment.universal.description'),
  },
  {
    key: 'intelligent',
    title: t('product1000bit.deployment.intelligent.title'),
    description: t('product1000bit.deployment.intelligent.description'),
  },
  {
    key: 'remote',
    title: t('product1000bit.deployment.remote.title'),
    description: t('product1000bit.deployment.remote.description'),
  },
])

// 轮播图数据
const originalImages = [case1Image, case2Image, case3Image]

// 为了实现无缝循环，在数组前后各添加一张图片
const carouselImages = ref([
  originalImages[originalImages.length - 1], // 最后一张图片放在开头
  ...originalImages,
  originalImages[0], // 第一张图片放在结尾
])

// 轮播图状态
const currentSlide = ref(1) // 从索引1开始（真正的第一张图片）
const autoPlayTimer = ref<NodeJS.Timeout | null>(null)
const isTransitioning = ref(false)

// 切换到指定幻灯片
const goToSlide = (index: number) => {
  if (isTransitioning.value) return
  currentSlide.value = index + 1 // 加1是因为数组开头有一张重复图片
  resetAutoPlay()
}

// 下一张幻灯片
const nextSlide = () => {
  if (isTransitioning.value) return

  isTransitioning.value = true
  currentSlide.value++

  // 如果到达了复制的第一张图片（数组最后一个位置）
  if (currentSlide.value >= carouselImages.value.length - 1) {
    setTimeout(() => {
      // 无动画跳转到真正的第一张图片
      const carousel = document.querySelector('.carousel-track') as HTMLElement
      if (carousel) {
        carousel.style.transition = 'none'
        currentSlide.value = 1
        setTimeout(() => {
          carousel.style.transition = 'transform 0.5s ease-in-out'
          isTransitioning.value = false
        }, 50)
      }
    }, 500) // 等待动画完成
  } else {
    setTimeout(() => {
      isTransitioning.value = false
    }, 500)
  }
}

// 开始自动播放
const startAutoPlay = () => {
  autoPlayTimer.value = setInterval(() => {
    nextSlide()
  }, 5000)
}

// 重置自动播放
const resetAutoPlay = () => {
  if (autoPlayTimer.value) {
    clearInterval(autoPlayTimer.value)
  }
  startAutoPlay()
}

// 产品对比-上报友盟
const trackEventHeroButtons = (label: string) => {
  trackEvent('1000bit专用量子计算机', '点击', '产品对比按钮')
}
</script>

<template>
  <div class="min-h-screen w-full">
    <!-- web端 -->
    <div class="hidden md:block min-h-screen w-full">
      <!-- 1000bit产品介绍 -->
      <section class="bg-[#FFF] h-[800px] pt-[95px] fade-in-section">
        <div class="max-w-[1200px] mx-auto">
          <div class="flex flex-col items-center justify-center">
            <h2
              class="text-[43px] font-medium leading-[65px] gradient-text mt-[12px] mb-[10px]"
            >
              {{ t('product1000bit.hero.title') }}
            </h2>
            <p
              class="text-[16px] text-[#000] leading-[26px] p-[10px] w-[818px] text-center mb-[21px] bg-[rgba(0,0,0,0.02)]"
            >
              <!-- {{ t('product1000bit.hero.description') }}
              <span class="text-[18px] font-bold text-[#000]">{{
                t('product1000bit.hero.description2')
              }}</span>
              {{ t('product1000bit.hero.description3') }} -->
              {{ t('product1000bit.hero.description4') }}
            </p>
            <!-- 动效占位 -->
            <div class="w-[670px] h-[378px] flex justify-center relative">
              <!-- 椭圆形阴影效果 -->
              <div
                class="absolute left-1/2 -translate-x-1/2 bottom-[65px] w-[1165px] h-[70px] machine-shadow"
              ></div>
              <div class="relative w-full h-full" style="z-index: 2">
                <!-- <ClientOnly>
                  <MachineModel
                    model-url="https://images-cloud.qboson.com/website/1000kmachine.glb"
                    height="500px"
                  />
                </ClientOnly> -->
                <img
                  src="https://images-cloud.qboson.com/website/1000bit/1000_jiqi1.png"
                  alt=""
                />
              </div>
            </div>
            <div class="relative z-20 flex items-center gap-[28px] mt-[10px]">
              <button
                class="rounded-[12px] px-[40px] py-[12px] text-[16px] text-[#333] leading-[24px] font-medium cursor-pointer gradient-border hover:opacity-80 transition-opacity"
                @click="openDialog"
              >
                {{ heroButtons[0].label }}
              </button>
              <button
                class="rounded-[12px] px-[40px] py-[12px] text-[16px] text-[#333] leading-[24px] font-medium cursor-pointer gradient-border hover:opacity-80 transition-opacity"
              >
                <NuxtLink
                  :to="heroButtons[1].to"
                  @click="trackEventHeroButtons(heroButtons[1].label)"
                >
                  {{ heroButtons[1].label }}
                </NuxtLink>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section class="min-h-[810px] fade-in-section">
        <video
          v-lazy-video
          :data-src="videoStability"
          poster="https://images-cloud.qboson.com/website/1000bit/poster.png"
          class="w-full h-full object-cover"
          style="background: transparent; aspect-ratio: 16/9"
          loop
          muted
          playsinline
          disablepictureinpicture
        ></video>
      </section>

      <!-- 产品性能 -->
      <section class="bg-[#F9FBFF] fade-in-section">
        <!-- 视频占位 -->
        <!-- <div class="w-full h-full">
          <img
            src="@/assets/images/product1000bit_bg_stability_video.png"
            alt=""
            class="w-full object-contain"
          />
        </div> -->
        <!-- 稳定性能 -->
        <div
          class="h-[579px] pt-[40px] pb-[26px] product1000bit-bg-stability"
          :style="{ backgroundImage: `url(${bgStability})` }"
        >
          <div class="max-w-[1200px] mx-auto">
            <h2
              class="text-[42px] text-[#000] leading-[35px] font-medium mb-[40px]"
            >
              {{ t('product1000bit.stability.title') }}
            </h2>
            <div class="flex">
              <div
                class="w-[346px] mt-[16px] mr-[39px] text-[20px] text-[#212121] leading-[34px] font-normal"
              >
                {{ t('product1000bit.stability.description') }}
              </div>
              <div
                class="w-[439px] h-[439px] flex-shrink-0 mr-[57px] product1000bit-bg-clock"
                :style="{ backgroundImage: `url(${bgClock})` }"
              >
                <!-- <video
                  v-lazy-video
                  :data-src="videoClock"
                  class="w-full h-full object-cover"
                  style="background: transparent"
                  loop
                  muted
                  playsinline
                  disablepictureinpicture
                ></video> -->
                <img
                  src="https://images-cloud.qboson.com/website/1000bit/24_time.png"
                  alt=""
                />
              </div>
              <div
                class="w-[346px] mt-[260px] text-[20px] text-[#212121] leading-[34px] font-normal"
              >
                {{ t('product1000bit.stability.uptime') }}
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 运行能耗(TODO:后期替换) -->
      <section
        class="w-full h-[655px] fade-in-section"
        style="
          background: linear-gradient(
            180deg,
            #9197a0 0%,
            #e0e1e2 calc(100% - 143px),
            #fff calc(100% - 143px),
            #9da1a8 100%
          );
        "
      >
        <div
          class="max-w-[1200px] h-full mx-auto relative flex justify-center items-center"
        >
          <div class="top-[90px] left-0 z-10" style="position: absolute">
            <div class="flex">
              <p class="flex flex-col">
                <span class="text-black text-[24px] font-normal">{{
                  t('product1000bit.energy.title')
                }}</span>
                <span class="text-black text-[24px] font-normal">
                  {{ t('product1000bit.energy.subtitle') }}
                </span>
              </p>
              <span
                class="pt-[10px] ml-[7px] text-[40px] font-medium leading-[80px]"
                style="
                  background: linear-gradient(
                    313deg,
                    #003b86 12.79%,
                    #3acaff 46.48%,
                    #56d9b3 67.78%,
                    #4cf4b3 92.11%
                  );
                  background-clip: text;
                  -webkit-background-clip: text;
                  -webkit-text-fill-color: transparent;
                "
              >
                1.2KW
              </span>
            </div>
            <p class="text-[#333] text-[18px] font-medium mb-[70px]">
              {{ t('product1000bit.energy.description') }}
            </p>
            <img
              class="w-[485px] h-[280px]"
              src="@/assets/images/1000bit/1000_energy.png"
              alt="1000bit相同行业耗能"
            />
          </div>
          <!-- <video
            v-lazy-video
            :data-src="video1000"
            loop
            muted
            playsinline
            disablepictureinpicture
          ></video> -->
          <img
            class="h-[83%]"
            src="https://images-cloud.qboson.com/website/1000bit/1000_gif.png"
            alt=""
          />
        </div>
      </section>

      <!-- 算法 -->
      <section class="w-full h-auto fade-in-section">
        <div
          class="pt-[120px] pb-[144px] product1000bit-bg-arithmetic"
          :style="{ backgroundImage: `url(${bgArithmetic})` }"
        >
          <div class="flex max-w-[1200px] mx-auto gap-[20px]">
            <div
              class="flex-1 max-w-[420px] h-[416px] cursor-pointer arithmetic-first-container bg-[#FFF] rounded-[30px] shadow-[4px_4px_12px_0_rgba(87,126,211,0.08)] hover:shadow-[20px_35px_80px_0px_rgba(0,0,0,0.08)] hover:rounded-[30px] transition-all duration-200 fade-in-item fade-in-delay-1"
            >
              <img
                src="@/assets/images/product1000bit_arithmetic_first.png"
                alt="算法第一张图"
                class="w-full h-full object-contain"
              />
            </div>

            <div
              class="flex-1 max-w-[340px] h-[416px] cursor-pointer arithmetic-second-container bg-[#FFF] rounded-[30px] shadow-[4px_4px_12px_0_rgba(87,126,211,0.08)] hover:shadow-[20px_35px_80px_0px_rgba(0,0,0,0.08)] hover:rounded-[30px] transition-all duration-200 fade-in-item fade-in-delay-2"
            >
              <img
                src="@/assets/images/product1000bit_arithmetic_second.png"
                alt="算法第二张图"
                class="w-full h-full object-contain"
              />
            </div>

            <div class="flex-1 max-w-[360px]">
              <div
                v-for="(feature, index) in features"
                :key="feature.key"
                class="flex flex-start fade-in-item"
                :class="[
                  { 'mb-[16px]': index < features.length - 1 },
                  `fade-in-delay-${index + 3}`,
                ]"
              >
                <img
                  src="@/assets/images/product1000bit_arithmetic_icon.png"
                  alt="icon"
                  class="w-[27px] h-[27px] object-contain"
                />
                <div class="ml-[20px]">
                  <h3
                    class="text-[20px] font-normal text-[#0D152E] mb-[11px] leading-[30px]"
                  >
                    {{ feature.title }}
                  </h3>
                  <p
                    class="text-[16px] text-[#7F8C8D] leading-[28px] font-normal"
                  >
                    {{ feature.description }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 易部署易使用 -->
      <section
        class="w-full fade-in-section"
        style="background: linear-gradient(180deg, #a5a9b1 0%, #fff 100%)"
      >
        <div class="pt-[72px] pb-[98px]">
          <div class="max-w-[1200px] mx-auto">
            <h2
              class="text-[40px] text-black leading-[40px] font-medium mb-[17px]"
            >
              {{ t('product1000bit.deployment.title') }}
            </h2>
            <div class="flex items-center">
              <div class="flex flex-col gap-[30px] w-[590px] mr-[18px]">
                <div
                  v-for="(feature, index) in deploymentFeatures"
                  :key="index"
                >
                  <p class="text-[20px] font-normal text-black leading-[30px]">
                    {{ feature.title }}{{ feature.description }}
                  </p>
                </div>
              </div>
              <!-- 视频占位 -->
              <div class="w-[684px] h-[415px] rounded-[12px]">
                <video
                  v-lazy-video
                  class="w-full h-full rounded-[12px] object-cover"
                  data-src="https://images-cloud.qboson.com/website/1000.mp4"
                  poster="@/assets/images/1000bit/1000.png"
                  loop
                  muted
                  playsinline
                  disablepictureinpicture
                ></video>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 行业高光案例 -->
      <section
        class="w-full pt-[133px] pb-[104px] bg-[#F9FBFF] fade-in-section"
      >
        <div class="max-w-[1200px] mx-auto">
          <div class="pl-[47px] mb-[29px] fade-in-item fade-in-delay-1">
            <h2 class="text-[36px] text-[#000] font-medium mb-[21px]">
              {{ t('product1000bit.cases.title') }}
            </h2>
            <p class="text-[22px] text-[#000] font-normal">
              {{ t('product1000bit.cases.drugDiscovery.description') }}
            </p>
          </div>

          <!-- 案例图片布局 -->
          <div class="cases-layout">
            <!-- 第一行：图1和图2 -->
            <div class="top-row relative">
              <div class="case-item case-1 fade-in-item fade-in-delay-2">
                <img
                  src="@/assets/images/product1000bit_case1.png"
                  alt="案例1"
                  class="w-full h-full object-cover rounded-lg"
                />
              </div>
              <div class="case-item case-2 fade-in-item fade-in-delay-3">
                <img
                  src="@/assets/images/product1000bit_case2.png"
                  alt="案例2"
                  class="w-full h-full object-cover rounded-lg"
                />
              </div>
              <!-- 叠加图片 -->
              <div class="absolute top-[-36px] left-[-59px] z-0">
                <img
                  src="@/assets/images/product1000bit_case6.png"
                  alt="案例2叠加"
                  class="w-full h-full object-cover rounded-lg"
                />
              </div>
              <!-- 叠加图片2 -->
              <div class="absolute bottom-[-49px] left-[521px] z-99">
                <img
                  src="@/assets/images/product1000bit_case7.png"
                  alt="案例2叠加"
                  class="w-full h-full object-cover rounded-lg"
                />
              </div>
            </div>

            <!-- 第二行：图3独占整行 -->
            <div class="middle-row relative">
              <div class="case-item case-3 fade-in-item fade-in-delay-4">
                <img
                  src="@/assets/images/product1000bit_case3.png"
                  alt="案例3"
                  class="w-full h-full object-cover rounded-lg"
                />
              </div>
              <!-- 叠加图片 -->
              <div class="absolute top-[-44px] right-[-60px] z-0">
                <img
                  src="@/assets/images/product1000bit_case6.png"
                  alt="案例2叠加"
                  class="w-full h-full object-cover rounded-lg"
                />
              </div>
            </div>

            <!-- 第三行：图4和图5 -->
            <div class="bottom-row">
              <div class="case-item case-4 fade-in-item fade-in-delay-5">
                <img
                  src="@/assets/images/product1000bit_case4.png"
                  alt="案例4"
                  class="w-full h-full object-cover rounded-lg"
                />
              </div>
              <div class="case-item case-5 fade-in-item fade-in-delay-6">
                <img
                  src="@/assets/images/product1000bit_case5.png"
                  alt="案例5"
                  class="w-full h-full object-cover rounded-lg"
                />
              </div>
            </div>

            <!-- 第四行：按钮 -->
            <div
              class="mt-[74px] flex justify-center fade-in-item fade-in-delay-7"
            >
              <NuxtLink
                :to="`/biopharmaceutical`"
                class="py-[12px] px-[40px] text-center rounded-[12px] cases-button"
              >
                {{ t('product1000bit.cases.drugDiscovery.title') }}
              </NuxtLink>
            </div>
          </div>
        </div>
      </section>
    </div>
    <!-- mobile端 -->
    <div class="md:hidden w-full">
      <!-- 产品介绍 -->
      <section
        class="w-full md:pt-[93px] pb-[40px] bg-[#F9FBFF] fade-in-section"
      >
        <!-- 动效占位 -->
        <div
          class="absolute left-1/2 -translate-x-1/2 top-[195px] w-[482px] h-[50px] machine-shadow"
        ></div>
        <div
          class="absolute flex justify-center items-center mx-auto h-[380px]"
        >
          <!-- <ClientOnly>
            <MachineModel
              model-url="https://images-cloud.qboson.com/website/1000kmachine.glb"
              height="330px"
              class="relative z-10"
            />
          </ClientOnly> -->
          <!-- <video
            v-lazy-video
            class="h-[80%] object-cover"
            :data-src="video1000bit"
            loop
            muted
            playsinline
            disablepictureinpicture
            controlsList="nodownload nofullscreen noremoteplayback"
            oncontextmenu="return false"
          ></video> -->
          <img
            src="https://images-cloud.qboson.com/website/1000bit/1000_jiqi1.png"
            alt=""
          />
        </div>
        <div class="pb-[21px] px-[16px] pt-[380px]">
          <h2
            class="text-[24px] text-[#000] font-medium leading-[36px] mb-[10px]"
          >
            {{ t('product1000bit.hero.title') }}
          </h2>
          <p class="text-[12px] text-[#666] font-normal">
            <!-- {{ t('product1000bit.hero.description')
            }}{{ t('product1000bit.hero.description2')
            }}{{ t('product1000bit.hero.description3') }} -->
            {{ t('product1000bit.hero.description4') }}
          </p>
        </div>
        <div class="flex items-center gap-[10px] px-[16px]">
          <button
            v-for="button in heroButtons"
            :key="button.key"
            class="px-[24px] py-[8px] text-[14px] text-[#000] leading-[22px] font-normal mobile-gradient-border hover:opacity-80 transition-opacity"
          >
            <NuxtLink :to="button.to">
              {{ button.label }}
            </NuxtLink>
          </button>
        </div>
      </section>
      <!-- 视频占位 -->
      <section class="w-[95%] min-h-[206px] mx-auto fade-in-section">
        <video
          v-lazy-video
          :data-src="videoStability"
          poster="https://images-cloud.qboson.com/website/1000bit/poster.png"
          class="w-full h-full rounded-[6px] object-cover"
          style="background: transparent"
          loop
          muted
          playsinline
          disablepictureinpicture
        ></video>
      </section>

      <!-- 稳定性能 -->
      <section
        class="w-full pt-[40px] pb-[38px] px-[16px] product1000bit-mobile-bg-stability fade-in-section"
        :style="{ backgroundImage: `url(${mobileBgStability})` }"
      >
        <h2
          class="text-[16px] text-[#000] leading-[16px] font-medium mb-[30px] text-center"
        >
          {{ t('product1000bit.stability.title') }}
        </h2>
        <div
          class="w-[213px] h-[213px] flex justify-center items-center mb-[30px] mx-auto product1000bit-mobile-bg-clock"
          :style="{ backgroundImage: `url(${mobileBgClock})` }"
        >
          <!-- <video
            v-lazy-video
            :data-src="videoClock"
            class="w-[213px] h-[213px] object-cover"
            loop
            muted
            playsinline
            disablepictureinpicture
          ></video> -->
          <img
            src="https://images-cloud.qboson.com/website/1000bit/24_time.png"
            alt=""
          />
        </div>
        <div
          class="text-[12px] text-[#666] leading-[18px] font-normal mb-[14px]"
        >
          {{ t('product1000bit.stability.description') }}
        </div>
        <div class="text-[12px] text-[#666] leading-[18px] font-normal">
          {{ t('product1000bit.stability.uptime') }}
        </div>
      </section>

      <!-- 运行能耗 -->
      <section class="w-full fade-in-section">
        <div class="w-full pt-[18px] pb-[12px] bg-[#F9FBFF] relative">
          <img
            src="@/assets/images/product1000bit_mobile_yxxn.png"
            alt=""
            class="w-full h-full object-contain"
          />
          <h2
            class="absolute top-[16px] left-[16px] text-[14px] text-[#000]"
          >
            {{ t('product1000bit.energy.title') }}<br />{{
              t('product1000bit.energy.subtitle')
            }}
          </h2>
          <p
            class="absolute top-[80px] left-[16px] text-[12px] text-[#666] font-normal"
          >
            {{ t('product1000bit.energy.description') }}
          </p>
        </div>
        <div class="w-full">
          <video
            v-lazy-video
            :data-src="video1000Mobile"
            class="w-full h-full object-contain"
            loop
            muted
            playsinline
            disablepictureinpicture
          ></video>
          <!-- <img
            src="@/assets/images/product1000bit_mobile_yxcp.png"
            alt=""
            class="w-full h-full object-contain"
          /> -->
        </div>
      </section>

      <!-- 算力 -->
      <section
        class="w-full h-auto py-[40px] px-[16px] product1000bit-mobile-bg-arithmetic fade-in-section"
        :style="{ backgroundImage: `url(${mobileBgArithmetic})` }"
      >
        <div class="overflow-x-auto mb-[32px]">
          <div class="flex gap-[10px] w-max">
            <div class="flex-shrink-0">
              <img
                src="@/assets/images/product1000bit_arithmetic_mobile_first.png"
                alt="icon"
                class="w-[310px] h-[332px] object-contain"
              />
            </div>
            <div class="flex-shrink-0">
              <img
                src="@/assets/images/product1000bit_arithmetic_mobile_second.png"
                alt="icon"
                class="w-[310px] h-[332px] object-contain"
              />
            </div>
          </div>
        </div>
        <div class="flex flex-col">
          <div
            v-for="(feature, index) in features"
            :key="feature.key"
            class="flex flex-start"
            :class="{ 'mb-[14px]': index < features.length - 1 }"
          >
            <img
              src="@/assets/images/product1000bit_arithmetic_icon.png"
              alt="icon"
              class="w-[20px] h-[20px] object-contain"
            />
            <div class="ml-[11px]">
              <h3
                class="text-[14px] font-medium text-[#000] mb-[11px] leading-[14px]"
              >
                {{ feature.title }}
              </h3>
              <p class="text-[12px] text-[#666] leading-[16px] font-normal">
                {{ feature.description }}
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- 易部署易使用 -->
      <section
        class="w-full py-[40px] px-[16px] fade-in-section"
        style="
          background: linear-gradient(
            rgb(165, 169, 177) 0%,
            rgb(255, 255, 255) 100%
          );
        "
      >
        <h2
          class="text-[16px] text-[#000] leading-[16px] font-medium mb-[24px] text-center"
        >
          {{ t('product1000bit.deployment.title') }}
        </h2>
        <div class="flex flex-col">
          <div
            v-for="(feature, index) in deploymentFeatures"
            :key="index"
            :class="{ 'mb-[14px]': index < deploymentFeatures.length - 1 }"
          >
            <p
              class="text-[14px] font-medium text-[#000] leading-[14px] mb-[6px]"
            >
              {{ feature.title }}
            </p>
            <p
              class="text-[12px] font-normal text-[#333] leading-[18px] pl-[24px]"
            >
              {{ feature.description }}
            </p>
          </div>
        </div>
        <!-- 视频占位 -->
        <div
          class="w-full h-[206px] mt-[24px] rounded-md mobile-video-placeholder"
        >
          <video
            v-lazy-video
            class="w-full h-full rounded-md"
            data-src="https://images-cloud.qboson.com/website/1000.mp4"
            loop
            muted
            playsinline
            disablepictureinpicture
          ></video>
        </div>
      </section>

      <!-- 行业高光案例 -->
      <section class="w-full py-[40px] px-[16px] bg-[#F9FBFF] fade-in-section">
        <h2 class="text-[16px] text-[#000] font-medium text-center mb-[14px]">
          {{ t('product1000bit.cases.title') }}
        </h2>
        <p class="text-[12px] text-[#666] font-normal text-center">
          {{ t('product1000bit.cases.drugDiscovery.description') }}
        </p>

        <div class="w-full flex flex-col items-center mt-[24px]">
          <!-- 轮播图片容器 - 显示完整图片+部分下一张 -->
          <div class="w-full max-w-[343px] h-[332px] overflow-hidden relative">
            <div
              class="carousel-track flex h-full transition-transform duration-500 ease-in-out"
              :style="{ transform: `translateX(-${currentSlide * 320}px)` }"
            >
              <div
                v-for="(image, index) in carouselImages"
                :key="index"
                class="w-[310px] h-[332px] flex-shrink-0 mr-[10px]"
              >
                <img
                  :src="image"
                  :alt="`案例图片 ${index + 1}`"
                  class="w-full h-full object-cover rounded-lg"
                />
              </div>
            </div>
          </div>

          <!-- 指示器 -->
          <div class="flex justify-center gap-[4px] mt-[24px]">
            <button
              v-for="(_, index) in originalImages"
              :key="index"
              class="w-[32px] h-[2px] border-0 cursor-pointer transition-all duration-300 carousel-indicator"
              :class="
                currentSlide - 1 === index
                  ? 'carousel-indicator-active'
                  : 'carousel-indicator-inactive'
              "
              @click="goToSlide(index)"
            ></button>
          </div>
        </div>
      </section>

      <!-- 行业高光案例2 -->
      <section
        class="w-full pt-[24px] px-[16px] pb-[40px] product1000bit_mobile_case_bg bg-[#F9FBFF] fade-in-section"
        :style="{ backgroundImage: `url(${mobileCaseBg})` }"
      >
        <div class="overflow-x-auto mb-[32px]">
          <div class="flex gap-[10px] w-max">
            <div class="flex-shrink-0">
              <img
                src="@/assets/images/product1000bit_mobile_case4.png"
                alt="icon"
                class="w-[310px] h-[290px] object-contain"
              />
            </div>
            <div class="flex-shrink-0">
              <img
                src="@/assets/images/product1000bit_mobile_case5.png"
                alt="icon"
                class="w-[310px] h-[290px] object-contain"
              />
            </div>
            <div class="flex-shrink-0">
              <img
                src="@/assets/images/product1000bit_mobile_case6.png"
                alt="icon"
                class="w-[310px] h-[290px] object-contain"
              />
            </div>
          </div>
        </div>
        <div class="flex justify-center">
          <NuxtLink
            :to="`/biopharmaceutical`"
            class="py-[12px] px-[40px] text-center rounded-[4px] cases-button"
          >
            {{ t('product1000bit.cases.drugDiscovery.title') }}
          </NuxtLink>
        </div>
      </section>
    </div>
    <!-- 业务咨询及商务合作弹窗 -->
    <ConsultationDialog v-model:show="showConsultDialog" />
  </div>
</template>

<style scoped>
/* 滚动淡入动画 */
.fade-in-section {
  opacity: 0;
  transform: translateY(30px);
  transition:
    opacity 0.8s ease-out,
    transform 0.8s ease-out;
}

.fade-in-visible {
  opacity: 1;
  transform: translateY(0);
}

/* 子元素顺序淡入动画 */
.fade-in-item {
  opacity: 0;
  transform: translateY(60px);
}

.fade-in-item-visible {
  opacity: 1;
  transform: translateY(0);
}

/* 延迟类 - 增加间隔让效果更明显 */
.fade-in-delay-1 {
  transition:
    opacity 0.8s ease-out 0.2s,
    transform 0.8s ease-out 0.2s;
}
.fade-in-delay-2 {
  transition:
    opacity 0.8s ease-out 0.5s,
    transform 0.8s ease-out 0.5s;
}
.fade-in-delay-3 {
  transition:
    opacity 0.8s ease-out 0.8s,
    transform 0.8s ease-out 0.8s;
}
.fade-in-delay-4 {
  transition:
    opacity 0.8s ease-out 1.1s,
    transform 0.8s ease-out 1.1s;
}
.fade-in-delay-5 {
  transition:
    opacity 0.8s ease-out 1.4s,
    transform 0.8s ease-out 1.4s;
}
.fade-in-delay-6 {
  transition:
    opacity 0.8s ease-out 1.7s,
    transform 0.8s ease-out 1.7s;
}
.fade-in-delay-7 {
  transition:
    opacity 0.8s ease-out 2s,
    transform 0.8s ease-out 2s;
}

/* 可以在这里添加自定义样式 */
/* video { */
/* display: block; */
/* background-color: #f5f5f5; */
/* object-fit: cover; */
/* } */

/* 渐变文字 */
.gradient-text {
  background: linear-gradient(
    44.487294800425836deg,
    #47b897 0%,
    #37beef 46%,
    #014396 90%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

/* 渐变边框圆角 */
.gradient-border {
  position: relative;
  background: #f9fbff;
  z-index: 1;
}

.gradient-border::before {
  content: '';
  position: absolute;
  inset: 0;
  padding: 1px;
  border-radius: 12px;
  background: linear-gradient(
    90deg,
    rgba(55, 187, 148, 1),
    rgba(1, 46, 128, 1)
  );
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  z-index: -1;
}

/* 稳定运行量子计算机模块style */
.product1000bit-bg-stability {
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

/* 算力模块style */
.product1000bit-bg-arithmetic {
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.product1000bit-bg-clock {
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

/* 案例布局 */
.cases-layout {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 1200px;
  margin: 0 auto;
}

/* 第一行：图1和图2，各590px * 590px */
.top-row {
  display: flex;
  gap: 20px;
  height: 590px;
}

.top-row .case-item {
  width: 590px;
  height: 590px;
  flex-shrink: 0;
}

/* 第二行：图3，1200px * 356px */
.middle-row {
  display: flex;
  height: 356px;
}

.middle-row .case-item {
  width: 1200px;
  height: 356px;
  flex-shrink: 0;
}

/* 第三行：图4(370px * 345px) 和 图5(810px * 345px) */
.bottom-row {
  display: flex;
  gap: 20px;
  height: 345px;
}

.bottom-row .case-4 {
  width: 370px;
  height: 345px;
  flex-shrink: 0;
}

.bottom-row .case-5 {
  width: 810px;
  height: 345px;
  flex-shrink: 0;
}

.case-item {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  background: #fff;
  position: relative;
  z-index: 10;
}

/* case-item 的 hover 效果单独处理 */
.case-item.fade-in-item-visible:hover {
  transform: translateY(-5px) !important;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
}

/* 叠加图片样式 - 显示在底层 */
.top-row .absolute,
.middle-row .absolute {
  z-index: 1;
}

/* 叠加图片2样式 - 显示在最顶层 */
.z-99 {
  z-index: 99 !important;
}

/* 案例按钮样式 */
.cases-button {
  background: linear-gradient(90deg, #36ba93 0%, #47c3e2 54%, #002d7f 100%);
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
}

/* .cases-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
} */

/* 移动端style */
.mobile-gradient-border {
  position: relative;
  background: #fff;
  z-index: 1;
}

.mobile-gradient-border::before {
  content: '';
  position: absolute;
  inset: 0;
  padding: 1px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    rgba(54, 186, 147, 1),
    rgba(71, 195, 225, 1),
    rgba(7, 60, 137, 1)
  );
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  z-index: -1;
}

.product1000bit-mobile-bg-stability {
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.product1000bit-mobile-bg-clock {
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.product1000bit-mobile-bg-arithmetic {
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.product1000bit_mobile_case_bg {
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

/* 移动端视频占位样式 */
.mobile-video-placeholder {
  box-shadow: 10px 18px 35px 0px rgba(0, 0, 0, 0.08);
}

/* 滑动容器样式 */
.overflow-x-auto {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  scroll-behavior: smooth;
}

.overflow-x-auto::-webkit-scrollbar {
  display: none; /* Chrome, Safari and Opera */
}

/* 轮播图指示器样式 */
.carousel-indicator {
  border-radius: 10px;
}

.carousel-indicator-active {
  background: linear-gradient(90deg, #41e0cb 0%, #043f9b 100%);
}

.carousel-indicator-inactive {
  background: #b8b8b8;
}

.carousel-indicator-inactive:hover {
  background: #a0a0a0;
}

/* 机器底部椭圆形阴影效果 */
.machine-shadow {
  border-radius: 50%;
  background: #fff;
  filter: blur(10.649999618530273px);
  box-shadow:
    0 0 60px 30px rgba(200, 200, 200, 0.3),
    0 0 100px 60px rgba(200, 200, 200, 0.2),
    0 0 140px 90px rgba(200, 200, 200, 0.1);
}
</style>
